/**
 * Static i18n bundle for the patch-log UI chrome (Issue 5).
 *
 * Engineering-owned: these are the FIXED strings of the modal and history page
 * (title, buttons, labels). Entry content (titles, points, alt text) is authored
 * per log in the backoffice and travels with the API payload — never here.
 *
 * Adding a 4th locale = add a member to `PatchLogLocale` (types.ts) and a block
 * below; the record type makes the compiler list every missing key. No schema
 * or API change is needed.
 *
 * MERGE GATE (ticket, Issue 5): the `en` and `zh` strings below need sign-off
 * from a native/fluent reviewer before this file ships. Do not treat the
 * current values as final copy.
 */
import type { PatchLogLocale } from './types';

export type PatchLogCopyKey =
  | 'title'
  | 'history'
  | 'confirm'
  | 'remindLater'
  | 'scrollHint'
  | 'before'
  | 'after'
  | 'area'
  | 'totalChanges'
  | 'close'
  | 'historyEmpty'
  | 'historyFailed'
  | 'historySubtitle'
  | 'imageViewer'
  | 'viewImage'
  | 'zoomHint'
  | 'zoomIn'
  | 'zoomOut'
  | 'resetZoom'
  | 'category.fitur_baru'
  | 'category.perbaikan'
  | 'category.tampilan_berubah'
  | 'version'
  | 'releasedOn'
  | 'language'
  | 'ackFailed'
  | 'deferFailed';

export const PATCH_LOG_COPY: Record<PatchLogLocale, Record<PatchLogCopyKey, string>> = {
  id: {
    title: 'Apa yang Baru di Lumen',
    history: 'Riwayat Pembaruan',
    confirm: 'Saya Mengerti',
    remindLater: 'Ingatkan Saya Nanti',
    scrollHint: 'Gulir ke bawah',
    before: 'SEBELUM',
    after: 'SESUDAH',
    area: 'Halaman',
    totalChanges: 'Total perubahan',
    close: 'Tutup',
    historyEmpty: 'Belum ada pembaruan yang dipublikasikan.',
    historyFailed: 'Riwayat pembaruan gagal dimuat. Silakan coba lagi.',
    historySubtitle: 'Semua pembaruan Lumen yang sudah dirilis, dari yang terbaru.',
    imageViewer: 'Penampil gambar',
    viewImage: 'Lihat gambar ukuran penuh',
    zoomHint: 'Gulir atau cubit untuk memperbesar, seret untuk menggeser',
    zoomIn: 'Perbesar',
    zoomOut: 'Perkecil',
    resetZoom: 'Atur ulang zoom',
    'category.fitur_baru': 'Fitur Baru',
    'category.perbaikan': 'Perbaikan',
    'category.tampilan_berubah': 'Tampilan Berubah',
    version: 'Versi',
    releasedOn: 'Dirilis',
    language: 'Bahasa',
    ackFailed: 'Konfirmasi gagal disimpan. Silakan coba lagi.',
    deferFailed: 'Pengingat gagal disimpan. Pembaruan ini akan muncul lagi saat login berikutnya.',
  },
  en: {
    title: "What's New in Lumen",
    history: 'Update History',
    confirm: 'I Understand',
    remindLater: 'Remind Me Later',
    scrollHint: 'Scroll down',
    before: 'BEFORE',
    after: 'AFTER',
    area: 'Page',
    totalChanges: 'Total changes',
    close: 'Close',
    historyEmpty: 'No updates have been published yet.',
    historyFailed: 'The update history could not be loaded. Please try again.',
    historySubtitle: 'Every released Lumen update, newest first.',
    imageViewer: 'Image viewer',
    viewImage: 'View full-size image',
    zoomHint: 'Scroll or pinch to zoom, drag to pan',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    resetZoom: 'Reset zoom',
    'category.fitur_baru': 'New Feature',
    'category.perbaikan': 'Improvement',
    'category.tampilan_berubah': 'New Look',
    version: 'Version',
    releasedOn: 'Released',
    language: 'Language',
    ackFailed: 'Your confirmation could not be saved. Please try again.',
    deferFailed: 'The reminder could not be saved. This update will show again at your next login.',
  },
  zh: {
    title: 'Lumen 更新内容',
    history: '更新历史',
    confirm: '我明白了',
    remindLater: '稍后提醒我',
    scrollHint: '向下滚动',
    before: '之前',
    after: '之后',
    area: '页面',
    totalChanges: '更新总数',
    close: '关闭',
    historyEmpty: '暂无已发布的更新。',
    historyFailed: '更新历史加载失败，请重试。',
    historySubtitle: 'Lumen 已发布的全部更新，最新的排在最前。',
    imageViewer: '图片查看器',
    viewImage: '查看大图',
    zoomHint: '滚动或双指缩放，拖动可平移',
    zoomIn: '放大',
    zoomOut: '缩小',
    resetZoom: '重置缩放',
    'category.fitur_baru': '新功能',
    'category.perbaikan': '改进',
    'category.tampilan_berubah': '界面更新',
    version: '版本',
    releasedOn: '发布于',
    language: '语言',
    ackFailed: '确认未能保存，请重试。',
    deferFailed: '提醒未能保存。此更新将在您下次登录时再次显示。',
  },
};

export const PATCH_LOG_FALLBACK_LOCALE: PatchLogLocale = 'id';

/** Resolves a chrome string, falling back to Indonesian and finally to the key itself. */
export const resolvePatchLogCopy = (locale: PatchLogLocale, key: PatchLogCopyKey): string =>
  PATCH_LOG_COPY[locale]?.[key] ?? PATCH_LOG_COPY[PATCH_LOG_FALLBACK_LOCALE][key] ?? key;

/** BCP 47 tags for date formatting per locale. */
export const PATCH_LOG_LOCALE_TAGS: Record<PatchLogLocale, string> = {
  id: 'id-ID',
  en: 'en-GB',
  zh: 'zh-CN',
};

/**
 * "2026-08-12" -> "12 Agu 2026" / "12 Aug 2026" / "2026年8月12日".
 * Parsed as a calendar date (not UTC) so the day never shifts with the viewer's timezone.
 */
export const formatPatchLogDate = (isoDate: string, locale: PatchLogLocale): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
  if (!match) {
    return isoDate ?? '';
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  try {
    return new Intl.DateTimeFormat(PATCH_LOG_LOCALE_TAGS[locale] ?? PATCH_LOG_LOCALE_TAGS.id, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  } catch {
    return match[0];
  }
};

/**
 * "2.14" and "v2.14" -> "Versi 2.14"; "Jarvis 2.14" stays as authored. The
 * label is free text, so the word is only added when it would not read twice
 * — and a bare "v" goes with it, since the word already says "version".
 * Labels are commonly authored v-prefixed: the CMS suggests the next one that
 * way, and a GitHub release version arrives as "v3.0.18".
 *
 * Shared by the modal and the history page so both print the same line.
 */
export const formatPatchLogVersionLabel = (versionLabel: string, versionWord: string): string => {
  const trimmed = (versionLabel ?? '').trim();
  const match = /^v?(\d.*)$/i.exec(trimmed);
  return match ? `${versionWord} ${match[1]}` : trimmed;
};
